import { FocusHandler } from "@noriginmedia/norigin-spatial-navigation"
import _ from "lodash"
import React, { memo, useEffect, useState } from "react"
import { useRef } from "react"
import { useLocation } from "react-router"
import { SearchResultNode } from "src/hooks/use-search-sliders"

import {
  LiveConcertType,
  ListSliderFieldsFragment,
  PageInfo,
  PublicationLevel,
  SliderLayout,
} from "../../generated/graphql"
import { QuerySlider } from "../../screens/Detail/DetailScreen"
import { ArrayElement } from "../../types"
import { s } from "../../utilities/common"
import { fontSizeFactor } from "../../utilities/constants"

import { GenericSlider, SliderRef } from "./GenericSlider"
import { Teaser, TeaserProps } from "./Teaser"
import { Collection } from "./Variants/Collection"
import { NavigationSlider } from "./Variants/NavigationSlider"

export type Sliders = {
  id: string
  title: string
  layout?: SliderLayout
  liveTitles?: Record<number, string> | undefined
  type: SliderType
  style: TeaserDimension | undefined
  node?: SearchResultNode | undefined
  totalResults: number
  pageInfo: Pick<PageInfo, "hasNextPage">
  teasers: TTeaser[]
}[]

export type LiveConcertExtract = Extract<
  ArrayElement<Exclude<ListSliderFieldsFragment["items"], undefined>>,
  { __typename: "LiveConcert" }
>

export type ContentType = ArrayElement<
  Exclude<QuerySlider["items"], undefined>
>["__typename"]

export type TTeaser = {
  id: string
  uniqueId: string
  slugV2?: string // CuratedContentLink
  title?: string
  subtitle?: string
  caption?: string
  liveConcert?: LiveConcertExtract
  label?: string
  picture?: string
  publicationLevel: PublicationLevel
  liveConcertType?: LiveConcertType
  type?: ContentType | undefined
  progress?: number
}

export enum SliderType {
  Collection = "Collection",
  Vertical = "Vertical",
  Navigation = "Navigation",
}

export enum TeaserDimension {
  LiveTeaser = "LiveTeaser",
  LandscapeLargeTeaser = "LandscapeLargeTeaser",
  LandscapeSmallTeaser = "LandscapeSmallTeaser",
  SquareSmallTeaser = "SquareSmallTeaser",
  AvatarBox = "AvatarBox",
  AvatarLabel = "AvatarLabel",
  LandscapeSmallTeaserNoText = "LandscapeSmallTeaserNoText",
  WeeklyTop10 = "WeeklyTop10",
}

export const TeaserDimensions = {
  [TeaserDimension.LiveTeaser]: {
    width: s(405),
    height: (s(557) - s(227)) * fontSizeFactor + s(227),
    imageHeight: s(227),
    colSpacing: s("36px"),
  },
  [TeaserDimension.LandscapeLargeTeaser]: {
    width: s(552),
    height: (s(552) - s(310)) * fontSizeFactor + s(310),
    imageHeight: s(310),
    colSpacing: s("36px"),
  },
  [TeaserDimension.LandscapeSmallTeaser]: {
    width: s(405),
    height: (s(481) - s(227)) * fontSizeFactor + s(227),
    imageHeight: s(227),
    colSpacing: s("36px"),
  },
  [TeaserDimension.LandscapeSmallTeaserNoText]: {
    width: s(405),
    height: s(227),
    imageHeight: s(227),
    colSpacing: s("36px"),
  },
  [TeaserDimension.SquareSmallTeaser]: {
    width: s(316),
    height: (s(532) - s(316)) * fontSizeFactor + s(316),
    imageHeight: s(316),
    colSpacing: s("36px"),
  },
  [TeaserDimension.AvatarBox]: {
    width: s(316),
    height: (s(532) - s(258)) * fontSizeFactor + s(258),
    imageHeight: s(258),
    colSpacing: s("36px"),
  },
  [TeaserDimension.AvatarLabel]: {
    width: s(258),
    height: (s(444) - s(198)) * fontSizeFactor + s(198),
    imageHeight: s(198),
    colSpacing: s("36px"),
  },
  [TeaserDimension.WeeklyTop10]: {
    width: s(484),
    height: (s(648) - s(484)) * fontSizeFactor + s(484),
    imageHeight: s(484),
    colSpacing: s("36px"),
  },
}

export interface Props {
  dimensions?: TeaserDimension
  title: string
  data: ArrayElement<Sliders>
  margin?: boolean
  onFocus?: FocusHandler
  onPressTitle: TeaserProps["onPress"]
  onPressBrowse: () => void
  prefix?: string
}

export const Slider = ({ data, ...rest }: Props) => {
  const location = useLocation()
  const [locationResolve, setLocationResolve] = useState<undefined | string>()

  // don't change the key when the slider is updated. This can cause reloads on the main page for example to have the wrong path prefix
  useEffect(() => {
    if (!locationResolve) setLocationResolve(location.pathname)
  }, [location])

  if (!data || data.teasers.length === 0 || !locationResolve) return null

  return (
    <>
      {data.type === SliderType.Collection ? (
        <Collection data={data} path={locationResolve} {...rest} />
      ) : data.type === SliderType.Navigation ? (
        <NavigationSlider data={data} {...rest} />
      ) : (
        <MemoSlider data={data} path={locationResolve} {...rest} />
      )}
    </>
  )
}

const MemoSlider = memo(
  ({
    title,
    data,
    dimensions,
    margin,
    onFocus,
    onPressTitle,
    onPressBrowse,
    path,
    prefix,
  }: Props & { path: string }) => {
    const ref = useRef<SliderRef>(null)
    const [ariaLabelPrefix, setAriaLabelPrefix] = useState(title)

    if (!dimensions) return null

    return (
      <GenericSlider
        ref={ref}
        title={title}
        liveTitles={data.liveTitles}
        dimensions={dimensions}
        margin={margin}
        onFocus={onFocus}
        onBlur={() => setAriaLabelPrefix(title)}
      >
        {data.teasers &&
          data.teasers.map((item, i) => (
            <Teaser
              ariaLabelPrefix={ariaLabelPrefix}
              onBlur={() => setAriaLabelPrefix("")}
              key={data.id + data.title + item.uniqueId + i}
              onPress={onPressTitle}
              scrollDirection={"horizontal"}
              dimensions={dimensions}
              data={item as TTeaser}
              me={path + prefix + data.id + data.title + item.uniqueId}
              scrollView={ref}
              index={i}
              sliderLayout={data.layout}
            />
          ))}

        {data.teasers && data.pageInfo.hasNextPage && (
          <Teaser
            key={data.id + "browseall" + data.teasers.length}
            onPress={onPressBrowse}
            scrollDirection={"horizontal"}
            dimensions={dimensions}
            sliderId={title + data.id}
            me={path + prefix + data.id + data.title + "browseall"}
            scrollView={ref}
            index={data.teasers.length}
            sliderLayout={data.layout}
          />
        )}
      </GenericSlider>
    )
  },
  (prevProps, nextProps) => _.isEqual(prevProps.data, nextProps.data),
)
